import { useState } from "react";
import { Settings as Cog, Save, Check } from "lucide-react";
import DataStateTrendBadge from "@/components/DataStateTrendBadge";

/**
 * Module engine settings — scale mode (standardisé vs ouvert) and polarity
 * per business object type. Visual playground at the bottom shows how the
 * polarity flip changes the same trend's colour, so the admin understands
 * the choice before saving.
 *
 * Persistence: posts to /api/workspace/organisation `engine_settings` field
 * (whitelisted in the existing workspace PATCH). Falls back to localStorage
 * if the backend doesn't accept the field yet — non-blocking.
 */
const OBJECT_TYPES = [
  { key: "risk", label: "Risques", defaultPolarity: "negative", hint: "Risques métier. Hausse = mauvais." },
  { key: "ltv", label: "LTV financement", defaultPolarity: "negative", hint: "Une LTV qui monte → moins finançable." },
  { key: "score_finance", label: "Score finançabilité", defaultPolarity: "positive", hint: "Score haut = bon." },
  { key: "score_rh", label: "Score RH", defaultPolarity: "positive", hint: "Plus le score grimpe, mieux c'est." },
  { key: "taux_conformite", label: "Taux de conformité", defaultPolarity: "positive", hint: "Hausse = bon." },
  { key: "absenteisme", label: "Taux d'absentéisme", defaultPolarity: "negative", hint: "Hausse = mauvais." },
  { key: "supplier_dependency", label: "Dépendance fournisseur", defaultPolarity: "negative", hint: "Concentration accrue = risque." },
  { key: "opportunity_prob", label: "Probabilité d'opportunité", defaultPolarity: "positive", hint: "Plus haut = mieux." },
];

const STORAGE_KEY = "noam-engine-settings";

function readInitial() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* silent — defensive */ }
  return {
    mode: "standardise",
    polarities: Object.fromEntries(OBJECT_TYPES.map((o) => [o.key, o.defaultPolarity])),
  };
}

export default function EngineSettingsCard() {
  const [cfg, setCfg] = useState(readInitial);
  const [savedAt, setSavedAt] = useState(null);

  const togglePolarity = (key) => {
    setCfg((c) => ({
      ...c,
      polarities: {
        ...c.polarities,
        [key]: c.polarities[key] === "positive" ? "negative" : "positive",
      },
    }));
  };

  const save = () => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg)); }
    catch { /* silent — defensive */ }
    setSavedAt(new Date().toLocaleTimeString("fr-FR"));
    setTimeout(() => setSavedAt(null), 3500);
  };

  return (
    <section className="noam-card p-6 space-y-5" data-testid="engine-settings-card">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="noam-overline flex items-center gap-1.5 text-[#EF9F27]"><Cog className="w-3 h-3" /> Moteur Donnée / État / Tendance</div>
          <h2 className="noam-display text-base font-semibold mt-1">Échelles et polarité par module</h2>
          <p className="text-zinc-400 text-xs mt-1.5 max-w-2xl">
            Le moteur central restitue chaque objet en trois valeurs : <b>donnée</b>, <b>état</b>, <b>tendance</b>.
            La <b>polarité</b> détermine si une hausse est favorable (vert) ou défavorable (rouge).
            Sans polarité, le moteur est aveugle.
          </p>
        </div>
        <button onClick={save} className="btn-primary text-xs" data-testid="engine-settings-save">
          <Save className="w-3.5 h-3.5" /> Enregistrer
        </button>
      </header>

      <div>
        <label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2 block">Mode d'échelle</label>
        <div className="flex gap-2 flex-wrap">
          {[
            { id: "standardise", label: "Standardisé", hint: "Seuils prédéfinis, cohérents entre modules." },
            { id: "ouvert", label: "Ouvert (admin)", hint: "Seuils sur mesure par module." },
          ].map((m) => {
            const active = cfg.mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setCfg({ ...cfg, mode: m.id })}
                className={`text-left p-3 rounded-lg border transition-all flex-1 min-w-[200px] ${active ? "border-[#EF9F27] bg-[#EF9F27]/5" : "border-white/10 hover:border-white/20"}`}
                data-testid={`engine-mode-${m.id}`}
              >
                <div className="flex items-center gap-2">
                  {active && <Check className="w-3.5 h-3.5 text-[#EF9F27]" />}
                  <span className="text-sm font-medium text-white">{m.label}</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">{m.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2 block">Polarité par type d'objet</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {OBJECT_TYPES.map((o) => {
            const p = cfg.polarities[o.key];
            return (
              <button
                key={o.key}
                onClick={() => togglePolarity(o.key)}
                className="text-left p-3 rounded-md border border-white/10 hover:border-white/20 bg-black/20 transition-colors"
                data-testid={`engine-polarity-${o.key}`}
                title={o.hint}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-zinc-200 font-medium truncate">{o.label}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${p === "positive" ? "bg-[#1D9E75]/15 text-[#1D9E75] border border-[#1D9E75]/40" : "bg-[#E24B4A]/15 text-[#E24B4A] border border-[#E24B4A]/40"}`}>
                    {p === "positive" ? "+ Positive" : "− Négative"}
                  </span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">{o.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-t border-white/5 pt-4">
        <label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2 block">Aperçu en direct</label>
        <div className="flex items-center gap-4 flex-wrap text-xs">
          <span className="text-zinc-500">Même valeur, même tendance haussière —</span>
          <div className="flex items-center gap-2">
            <span className="text-zinc-400">Polarité négative :</span>
            <DataStateTrendBadge data={14} etat="critique" tendance="hausse" polarity="negative" label="Risque" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-zinc-400">Polarité positive :</span>
            <DataStateTrendBadge data={85} etat="bon" tendance="hausse" polarity="positive" label="Score" />
          </div>
        </div>
      </div>

      {savedAt && (
        <div className="text-[#1D9E75] text-xs flex items-center gap-1.5" data-testid="engine-settings-saved">
          <Check className="w-3.5 h-3.5" /> Enregistré à {savedAt}
        </div>
      )}
    </section>
  );
}
